/**
 * Every signature in this file is the endpoint contract.
 *
 * The UI never reaches into the store directly for data it would fetch from a
 * server. It calls these, they return Promises, and the delay is real. When the
 * real API arrives, lib/api.ts re-exports that module instead and nothing above
 * this line changes.
 */

import type {
  Account,
  ActivityEvent,
  AddOn,
  Claim,
  DbRow,
  DbTable,
  Deploy,
  DetailLevel,
  Environment,
  ErrorGroup,
  FileObj,
  Health,
  Project,
  Secret,
  Timeline,
  Traffic,
  TrafficWindow,
  Usage,
} from '../types';
import { delay } from './latency';
import { trafficFor } from './seed';
import { useDemoStore } from './store';

function state() {
  return useDemoStore.getState();
}

function speed() {
  return state().prefs.speed;
}

async function read<T>(value: () => T, delayOpts?: { min?: number; max?: number }): Promise<T> {
  await delay({ speed: speed(), ...delayOpts });
  return value();
}

/**
 * The single accessor every screen reads through. No screen may reach for a
 * project's deploys directly. When Preview environments arrive this grows a
 * second key, not nine rewritten screens.
 */
export function environmentOf(project: Project): Environment {
  return project.environments.production;
}

/* --------------------------------------------------------------- account */

export function getAccount(): Promise<Account> {
  return read(() => state().account);
}

export async function setDetailLevel(level: DetailLevel): Promise<Account> {
  await delay({ min: 60, max: 140, speed: speed() });
  const next = { ...state().account, detailLevel: level };
  useDemoStore.setState({ account: next });
  return next;
}

/* -------------------------------------------------------------- projects */

export function listProjects(): Promise<Project[]> {
  return read(() => state().projects, { min: 100, max: 200 });
}

export function getProject(slug: string): Promise<Project | null> {
  return read(() => state().projects.find((p) => p.slug === slug) ?? null, { min: 100, max: 200 });
}

const NO_HEALTH: Health = { downMinutes: 0, requestsLastHour: 0, errorsLastHour: 0 };

export function getProjectHealth(projectId: string): Promise<Health> {
  return read(() => state().projects.find((p) => p.id === projectId)?.health ?? NO_HEALTH, {
    min: 100,
    max: 200,
  });
}

/* --------------------------------------------------------------- deploys */

export function listDeploys(projectId: string): Promise<Deploy[]> {
  return read(() => state().deploys.filter((d) => d.projectId === projectId), { min: 600, max: 900 });
}

export function getDeploy(id: string): Promise<Deploy | null> {
  return read(() => state().deploys.find((d) => d.id === id) ?? null, { min: 500, max: 800 });
}

/* -------------------------------------------------------------- database */

export function listTables(projectId: string): Promise<DbTable[]> {
  return read(() => state().tables.filter((t) => t.projectId === projectId), { min: 200, max: 350 });
}

// `projectId` is unused by the fixture (table names don't collide across the
// mock's seeded projects) but is part of the contract real-api.ts needs to
// scope a real gateway read (see real-api.ts's header).
export function listRows(_projectId: string, tableName: string): Promise<DbRow[]> {
  return read(() => state().rows[tableName] ?? [], { min: 250, max: 400 });
}

/* ----------------------------------------------------------------- files */

export function listFiles(projectId: string): Promise<FileObj[]> {
  return read(() => state().files.filter((f) => f.projectId === projectId), { min: 450, max: 650 });
}

/* ------------------------------------------------------------------ keys */

export function listSecrets(projectId: string): Promise<Secret[]> {
  return read(() => state().secrets.filter((s) => s.projectId === projectId), { min: 300, max: 450 });
}

/* --------------------------------------------------------------- staging */

/**
 * What the settings screen needs to know about an app's staging environment.
 *
 * Derived from the seed rather than stubbed, so the demo tells the truth about
 * the case that matters: which keys production has that staging does not. The
 * comparison ignores the reserved ones, because those are minted per
 * environment and are never the owner's to add.
 */
export function getStagingReadiness(projectId: string): Promise<{
  stagingReady: boolean;
  workflowOk: boolean;
  workflowProblem: string | null;
  missingSettingNames: string[];
  deployMode: 'direct' | 'staged';
}> {
  return read(
    () => {
      const data = state();
      const project = data.projects.find((p) => p.id === projectId);
      const staging = data.projects.find(
        (p) => p.environment === 'staging' && p.slug === `${project?.slug}-staging`,
      );
      const named = (id: string | undefined) =>
        id
          ? data.secrets.filter((sec) => sec.projectId === id && !sec.reserved).map((sec) => sec.key)
          : [];
      const onStaging = new Set(named(staging?.id));
      return {
        stagingReady: !!staging,
        workflowOk: !!staging,
        workflowProblem: staging
          ? null
          : 'Your repository is not set up to promote a tagged version yet.',
        missingSettingNames: named(project?.id)
          .filter((key) => !onStaging.has(key))
          .sort(),
        deployMode: project?.deployMode ?? 'direct',
      };
    },
    { min: 250, max: 400 },
  );
}

/* --------------------------------------------------------------- add-ons */

export function listAddOns(projectId: string): Promise<AddOn[]> {
  return read(() => state().addons.filter((a) => a.projectId === projectId), { min: 400, max: 600 });
}

/* -------------------------------------------------- activity and errors */

export function listActivity(projectId: string): Promise<ActivityEvent[]> {
  return read(() => state().activity.filter((e) => e.projectId === projectId), { min: 1300, max: 1800 });
}

export function listErrors(projectId: string): Promise<ErrorGroup[]> {
  return read(() => state().errors.filter((e) => e.projectId === projectId), { min: 850, max: 1200 });
}

/* ----------------------------------------------------------------- usage */

export function getUsage(projectId: string | null): Promise<Usage | null> {
  return read(() => state().usage.find((u) => u.projectId === projectId) ?? null, { min: 1100, max: 1500 });
}

/* --------------------------------------------------------------- traffic */

/**
 * One window of traffic for one project. One read, one paint, no fan out.
 *
 * The screen has four families on it and it must not make four calls for them.
 * Four calls is four loading states, four failure modes and four chances to
 * paint a page that is half right in front of somebody who came here worried.
 * Everything the screen draws comes back from this, including the deploy marks
 * under the chart, which the platform already has in its own deploy table and
 * should never make the browser go and join for itself.
 *
 * `null` here is the project not existing, which is the same not found notice
 * every other project scoped read drives. `null` on a family INSIDE the
 * response is a different statement entirely and is documented on the type.
 *
 * The join happens here rather than in the browser, and the error groups are
 * part of it: the crash count on this screen and the group list on the
 * activity screen come off one pipeline in the real product, so they are read
 * out of one record here. A reviewer can add up the error list and get the
 * crash count, which is the only reason either number is worth printing.
 *
 * The derivation is pure and lives in seed.ts, which is where the three rules
 * it has to hold are written down: the last bucket of the 24h series is the
 * same pair of numbers the overview lamp is reading, the thirty day total is
 * the same requests number the usage screen is already printing, and the crash
 * count is the error groups, counted.
 */
export function getTraffic(projectId: string, window: TrafficWindow): Promise<Traffic | null> {
  return read(() => {
    const world = state();
    const project = world.projects.find((p) => p.id === projectId);
    if (!project) return null;
    return trafficFor(
      project,
      world.deploys.filter((d) => d.projectId === projectId),
      world.errors.filter((e) => e.projectId === projectId),
      world.usage.find((u) => u.projectId === projectId) ?? null,
      window,
    );
  });
}

/* ------------------------------------------------------------ graduation */

export function getClaim(token: string): Promise<Claim | null> {
  return read(() => state().claims.find((c) => c.token === token) ?? null);
}

/* ------------------------------------------------------------- timelines */

export function listTimelines(): Promise<Timeline[]> {
  return read(() => state().timelines);
}

/** Arms a run. Progress is computed from the clock, never pushed. */
export async function startRun(input: Omit<Timeline, 'speed'>): Promise<Timeline> {
  await delay({ min: 80, max: 160, speed: speed() });
  state().startTimeline(input);
  const started = state().timelines.find((t) => t.id === input.id);
  if (!started) throw new Error('timeline did not start');
  return started;
}
